<script setup lang="ts">
/* eslint-disable perfectionist/sort-imports -- the datepicker base stylesheet must load before the local styles overriding it */
import '@vuepic/vue-datepicker/dist/main.css';
import { Calendar, ChevronDown, ChevronUp, Clock, X } from 'lucide';
import type { FilterConfig, PickerSection } from '@vuepic/vue-datepicker';
import { computed, ref } from 'vue';
import { formatISO, parseISO } from '@legacy/js/date/date-fns.js';
import DateLocale from '@legacy/js/date/DateLocale.js';
import type { HTMLAttributes } from 'vue';
import type { ControlWidth } from '@/components/base/_internal/control';
import { controlWidthClass, provideFieldControl, useControlWidth, useFieldControl } from '@/components/base/_internal/control';
import SphButton from '@/components/base/button/';
import SphIcon from '@/components/base/icon';
import SphInput from '@/components/base/input/';
import { VueDatePicker } from '@vuepic/vue-datepicker';
import { getCurrentLocale } from '@legacy/js/service/LanguageHelper.js';
import { useI18n } from '@/composables/i18n';
import { cn } from '@/utils/shadcn/lib/utils';

defineOptions({
    compatConfig: {
        MODE: 3,
    },
});

const props = withDefaults(defineProps<Props>(), {
    class: '',
    variant: 'single',
    timeInput: false,
    timePicker: false,
    modelType: 'date-object',
    timezone: undefined,
    name: '',
    id: '',
    clearable: false,
    placeholder: undefined,
    required: false,
    invalid: false,
    disabled: false,
    inputAttrs: undefined,
    width: undefined,
    minDate: undefined,
    maxDate: undefined,
    allowedDates: undefined,
    disabledDates: undefined,
    filters: undefined,
    startTime: undefined,
});
type SingleModelValue = Date | string | null;
type RangeModelValue = [Date | string, Date | string] | null;
type ModelValue = SingleModelValue | RangeModelValue;

interface Props {
    class?: HTMLAttributes['class']
    variant?: 'single' | 'range'
    timeInput?: boolean
    timePicker?: boolean
    modelType?: 'date-object' | 'iso-atom' | 'date-string' | 'time-string'
    timezone?: string
    name?: string
    id?: string
    clearable?: boolean
    placeholder?: string
    required?: boolean
    invalid?: boolean
    disabled?: boolean
    inputAttrs?: Record<string, any>
    /** Falls back to the width of the format: a time, a date or a range fits exactly. */
    width?: ControlWidth
    minDate?: Date | string
    maxDate?: Date | string
    allowedDates?: (Date | string)[]
    disabledDates?: (Date | string)[] | ((_date: Date) => boolean)
    filters?: Partial<FilterConfig>
    /** The time a freshly picked day starts with — an "until" field wants the end of the day, not its first minute. */
    startTime?: Time
}

const model = defineModel<ModelValue>({ default: null });

const { i18n } = useI18n();

const width = useControlWidth(() => props.width, 'formatted');
const field = useFieldControl(() => props, () => width.value);

// The input inside is wired from here, so it must not pick up the field a second time.
provideFieldControl(null);

const modelType = computed(() => {
    if (props.timePicker) {
        return 'time-string';
    }
    return props.modelType;
});

const placeholder = computed(() => {
    if (props.placeholder !== undefined) {
        return props.placeholder;
    }

    if (props.timePicker) {
        return '';
    }

    return props.variant === 'range'
        ? i18n.t('Zeitraum auswählen')
        : i18n.t('Datum auswählen');
});

const locale = computed(() => getCurrentLocale());
const localeObject = computed(() => DateLocale.getLocale(locale.value));

const inputAttrs = computed(() => ({
    autocomplete: props.timePicker || props.variant === 'range' ? 'off' : 'on',
    placeholder: placeholder.value,
    id: field.value.id,
    'aria-describedby': field.value.describedBy,
    disabled: field.value.disabled,
    required: field.value.required,
    invalid: field.value.invalid,
    name: props.name,
    ...(props.inputAttrs || {}),
}));

function toDate(val: Date | string | null | undefined): Date | null {
    if (val == null) {
        return null;
    }

    if (typeof val === 'string') {
        if (modelType.value === 'time-string') {
            const [hours, minutes] = val.split(':').map(Number);
            if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
                return new Date(0, 0, 0, hours, minutes);
            }
            console.warn('Invalid time string format for modelValue:', val);
            return null;
        }
    }

    if (val instanceof Date) {
        const d = new Date(val);
        if (!props.timeInput) {
            d.setHours(0, 0, 0, 0);
        }
        return d;
    }

    try {
        const d = parseISO(val);
        if (d && !Number.isNaN(d.getTime())) {
            if (!props.timeInput) {
                d.setHours(0, 0, 0, 0);
            }
            return d;
        }
    } catch (e) {
        console.error('Error parsing date:', e);
    }

    return null;
}

function toExternal(value: Date | Time): Date | string {
    if (modelType.value === 'time-string') {
        const time = value as Time;
        const h = String(time.hours).padStart(2, '0');
        const m = String(time.minutes).padStart(2, '0');
        return `${h}:${m}`;
    }

    if (modelType.value === 'iso-atom') {
        try {
            return formatISO(value as Date);
        } catch (e) {
            console.error('Error formatting date to ISO string:', e);
            return '';
        }
    }

    if (modelType.value === 'date-string') {
        // toISOString() is UTC, which east of Greenwich turns a picked local midnight into the day before.
        return formatISO(value as Date, { representation: 'date' });
    }

    if (modelType.value === 'date-object') {
        return value as Date;
    }

    return value as Date;
}

export interface Time {
    hours: number
    minutes: number
}

const internalValue = computed<Date | [Date, Date] | Time | null>(() => {
    const val = model.value;

    if (props.timePicker) {
        if (val == null || val === '') {
            return null;
        }

        if (typeof val === 'object' && 'hours' in val && 'minutes' in val) {
            return val as Time;
        }

        if (typeof val === 'string') {
            const [hours, minutes] = val.split(':').map(Number);
            if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
                return { hours, minutes };
            }
        }

        console.warn('Invalid modelValue for time picker:', val);
        return null;
    }

    if (Array.isArray(val)) {
        const start = toDate(val[0]);
        const end = toDate(val[1]);
        return start && end ? [start, end] : null;
    }

    return toDate(val);
});

function handleUpdate(val: Date | Date[] | Time | null) {
    if (val == null) {
        model.value = null;
        return;
    }
    if (Array.isArray(val)) {
        model.value = [toExternal(val[0]), toExternal(val[1])];
        return;
    }
    model.value = toExternal(val);
}

const minDate = computed(() => toDate(props.minDate));
const maxDate = computed(() => toDate(props.maxDate));

const allowedDates = computed(() => {
    if (props.allowedDates === undefined) {
        return undefined;
    }
    return props.allowedDates
        .map((date) => toDate(date))
        .filter((date): date is Date => date !== null);
});

const disabledDates = computed(() => {
    if (props.disabledDates === undefined) {
        return undefined;
    }
    if (typeof props.disabledDates === 'function') {
        return props.disabledDates;
    }
    return props.disabledDates
        .map((date) => toDate(date))
        .filter((date): date is Date => date !== null);
});

const minInputWidthCharacters = computed(() => {
    let chars = 10;

    if (props.timePicker) {
        chars = 7;
    } else if (props.variant === 'range') {
        chars = 16;

        if (props.timeInput) {
            chars = 22;
        }
    } else if (props.variant === 'single') {
        chars = 11;

        if (props.timeInput) {
            chars = 17;
        }
    }

    return chars;
});

// The natural width fits the placeholder as well, which is often longer than the format. A `ch` is
// the width of a digit, and letters run wider, hence the two to spare.
const inputWidthCharacters = computed(() => (width.value === 'legacy'
    ? minInputWidthCharacters.value
    : Math.max(minInputWidthCharacters.value, placeholder.value.length + 2)));

const config = computed(() => {
    if (modelType.value === 'date-string' && props.timeInput) {
        console.warn('Configuration error: using modelType "date-string" with timeInput.');
    }

    return {
        range: props.variant === 'range'
            ? {
                    partialRange: false,
                    showLastInRange: true,
                    autoSwitchStartEnd: true,
                }
            : false,
        textInput: {
            maskFormat: props.timePicker ? 'hh:mm' : 'DD/MM/YYYY',
            enterSubmit: true,
            selectOnFocus: true,
            escClose: true,
            applyOnBlur: !props.timePicker,
        },
        formats: {
            input: props.timePicker ? 'HH:mm' : (props.timeInput ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy'),
        },
        timezone: props.timezone,
        autoApply: props.timePicker || !props.timeInput,
        locale: localeObject.value,
        timePicker: props.timePicker,
        minDate: minDate.value ?? undefined,
        maxDate: maxDate.value ?? undefined,
        allowedDates: allowedDates.value,
        disabledDates: disabledDates.value,
        filters: props.filters,
        preventMinMaxNavigation: true,
        timeConfig: {
            enableTimePicker: props.timeInput,
            is24: true,
            timePickerInline: true,
            startTime: props.timePicker
                ? { hours: 0, minutes: 0 }
                : props.variant === 'range'
                    ? [{ hours: 0, minutes: 0, seconds: 0 }, { hours: 0, minutes: 0, seconds: 0 }]
                    : { hours: props.startTime?.hours ?? 0, minutes: props.startTime?.minutes ?? 0, seconds: 0 },
        },
        flow: props.timePicker
            ? {
                    steps: ['hours', 'minutes'] as PickerSection[],
                }
            : undefined,
        inputAttrs: {
            clearable: false,
        },
        hideNavigation: props.timePicker ? ['hours', 'minutes', 'time'] as PickerSection[] : undefined,
        floating: {
            strategy: 'fixed' as const,
            placement: 'top' as const,
            shift: true,
            flip: {
                fallbackPlacements: ['right', 'bottom'] as ('right' | 'bottom')[],
            },
        },
        actionRow: {
            showSelect: false,
            showCancel: false,
        },
    };
});

const SLASH = /\//g;
const RANGE_SEPARATOR = /\s+-\s+/g;
const ALLOWED_KEY = /[0-9/.-]/;

function inputDisplayValue(value: string) {
    return value
        .replace(SLASH, '.')
        .replace(RANGE_SEPARATOR, '  —  ');
}

function handleFocus(ev: FocusEvent, isMenuOpen: boolean, openMenu: () => void, onFocus: () => void) {
    if (field.value.disabled) {
        return;
    }

    if (!isMenuOpen) {
        openMenu();
    }
    const input = ev.target;
    if (input instanceof HTMLInputElement) {
        input.select();
    }
    onFocus();
}

function handleBlur(ev: FocusEvent, onBlur: () => void) {
    if ((ev.relatedTarget as Element | null)?.closest('.dp--menu')) {
        return;
    }
    onBlur();
}

function handleKeypress(ev: KeyboardEvent, onKeypress: any) {
    // filter out non-numeric input except for slash, dot and hyphen
    if (!ALLOWED_KEY.test(ev.key)) {
        ev.preventDefault();
        return;
    }

    onKeypress(ev);
}

/**
 * VueDatePicker's flow only advances when a *different* value is selected.
 * If the user picks the same hour or minute that is already in the internal
 * state, updateFlowStep is skipped and the overlay closes without the picker
 * advancing or closing.
 *
 * Fix: track the currently-open overlay. After any flow overlay closes, wait
 * one macrotask (enough for any nextTick-scheduled overlay-open to fire).
 * - If hours closed and no new overlay opened → same hour was picked, force
 *   flow forward to minutes via executeFlow.
 * - If minutes closed and no new overlay opened → same minute was picked (or
 *   normal auto-apply didn't fire), force selectDate() to close.
 */
const dpRef = ref<InstanceType<typeof VueDatePicker> | null>(null);
const dpMenuOpen = ref(false);
const activeOverlay = ref<PickerSection | null>(null);

function handleOpen() {
    dpMenuOpen.value = true;
}

function handleClosed() {
    dpMenuOpen.value = false;
}

function handleOverlayToggle(toggle: { open: boolean, overlay: PickerSection }) {
    if (!props.timePicker) {
        return;
    }

    activeOverlay.value = toggle.open ? toggle.overlay : null;

    if (toggle.open) {
        return;
    }

    setTimeout(() => {
        if (!dpMenuOpen.value) {
            return;
        }

        if (toggle.overlay === 'hours' && activeOverlay.value === null) {
            dpRef.value?.executeFlow('minutes' as PickerSection);
        } else if (toggle.overlay === 'minutes' && activeOverlay.value === null) {
            dpRef.value?.selectDate();
        }
    });
}
</script>

<template>
    <!-- The width goes on a wrapper: .dp--main sets width: 100% outside any layer, which no utility class can beat. -->
    <div
        data-slot="date-picker"
        :class="cn(controlWidthClass(width), props.class)"
    >
        <VueDatePicker
            ref="dpRef"
            :key="String(modelType + props.timeInput)"
            v-bind="config"
            :model-value="internalValue"
            :disabled="field.disabled"
            @update:model-value="handleUpdate"
            @open="handleOpen"
            @closed="handleClosed"
            @overlay-toggle="handleOverlayToggle"
        >
            <template
                v-if="props.timeInput"
                #action-row="{ selectDate }"
            >
                <SphButton
                    :label="i18n.t('Übernehmen')"
                    size="md"
                    @click="selectDate"
                />
            </template>

            <template #tp-inline-arrow-up>
                <SphIcon
                    :icon="ChevronUp"
                    size="lg"
                    :svg-attributes="{
                        preserveAspectRatio: 'xMidYMid slice',
                    }"
                />
            </template>

            <template #tp-inline-arrow-down>
                <SphIcon
                    :icon="ChevronDown"
                    size="lg"
                    :svg-attributes="{
                        preserveAspectRatio: 'xMidYMid slice',
                    }"
                />
            </template>

            <template
                #dp-input="{
                    value,
                    onInput,
                    onEnter,
                    onTab,
                    onClear,
                    onBlur,
                    onFocus,
                    onKeypress,
                    onPaste,
                    openMenu,
                    closeMenu,
                    isMenuOpen,
                }"
            >
                <SphInput
                    type="text"
                    width="full"
                    :input-style="{ width: `${inputWidthCharacters}ch` }"
                    :model-value="inputDisplayValue(value)"
                    v-bind="inputAttrs"
                    @keydown="(ev: KeyboardEvent) => {
                        if (ev.key === 'Escape') { ev.stopPropagation(); closeMenu(); }
                        else if (ev.key === 'Tab') { onTab(ev); }
                        else if (ev.key === 'Enter') { ev.preventDefault(); closeMenu(); onEnter(ev); }
                    }"
                    @blur="handleBlur($event, onBlur)"
                    @focus="handleFocus($event, isMenuOpen, openMenu, onFocus)"
                    @keypress="handleKeypress($event, onKeypress)"
                    @paste="onPaste"
                    @input="onInput"
                >
                    <template #prefix>
                        <slot name="prefix">
                            <SphIcon
                                :icon="props.timePicker ? Clock : Calendar"
                                size="sm"
                            />
                        </slot>
                    </template>
                    <template #suffix>
                        <SphButton
                            v-if="props.clearable && !field.disabled"
                            :icon="X"
                            size="md"
                            :aria-label="i18n.t('Eingabe löschen')"
                            variant="transparent"
                            class="hover:text-accent duration-200"
                            :class="[value ? 'opacity-100' : 'opacity-0']"
                            @click="onClear"
                        />
                    </template>
                </SphInput>
            </template>
        </VueDatePicker>
    </div>
</template>

<style>
@reference '@/assets/styles/config.css';

.dp--theme-light {
    --dp-font-family: var(--font-mono);
    --dp-background-color: var(--color-popover, #fff);
    --dp-text-color: var(--color-gray-800);
    --dp-hover-color: var(--color-gray-200, #f3f3f3);
    --dp-hover-text-color: #212121;
    --dp-hover-icon-color: #959595;
    --dp-primary-color: var(--color-accent-light, #1d4ed8);
    --dp-primary-disabled-color: var(--color-gray-300, #6bacea);
    --dp-primary-text-color: var(--color-accent-light-foreground, #f8f5f5);
    --dp-secondary-color: #c0c4cc;
    --dp-border-color: var(--color-border, #ddd);
    --dp-menu-border-color: var(--color-popover-border, #ddd);
    --dp-border-radius: var(--radius-md, 0.375rem);
    --dp-menu-padding: 0.75rem;
    --dp-border-color-hover: #aaaeb7;
    --dp-border-color-focus: #aaaeb7;
    --dp-disabled-color: #f6f6f6;
    --dp-scroll-bar-background: #f3f3f3;
    --dp-scroll-bar-color: #959595;
    --dp-success-color: #76d275;
    --dp-success-color-disabled: #a3d9b1;
    --dp-icon-color: #959595;
    --dp-danger-color: #ff6f60;
    --dp-marker-color: #ff6f60;
    --dp-tooltip-color: #fafafa;
    --dp-disabled-color-text: #e0e0e0;
    --dp-highlight-color: rgb(25 118 210 / 10%);
    --dp-range-between-dates-background-color: var(--dp-hover-color, #f3f3f3);
    --dp-range-between-dates-text-color: var(--dp-hover-text-color, #212121);
    --dp-range-between-border-color: var(--dp-hover-color, #f3f3f3);
}

.dp--main {
    @apply flex-[1_1_auto];
}

.dp--calendar-header-item {
    @apply flex flex-col justify-center text-sm font-medium text-gray-700;
}

.dp--btn.dp--arrow-btn-nav .dp--icon {
    stroke: none;
}

.dp--arrow-top {
    @apply hidden;
}
.dp--menu {
    @apply shadow-md;
}

.dp--menu-inner {
    @apply w-fit font-sans;
}

.dp--cell-inner {
    @apply font-mono font-medium;
}

.dp--action-row {
    @apply pt-0 px-2.5;
}

.dp--time-col {
    @apply px-0;

    &:first-child,
    &:nth-child(3) {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: repeat(2, auto);
        gap: 0.25rem 0.25rem;
    }

    &:first-child {
        @apply pr-2;
    }

    &:nth-child(3) {
        @apply pl-2;
    }
}

.dp--time-display {
    @apply row-start-1 row-span-2 h-full p-0 text-xl leading-none text-center;
}

.dp--tp-inline-btn-top,
.dp--tp-inline-btn-bottom {
    @apply text-(--dp-icon-color) col-start-2 h-auto;

    .sph-icon {
        --sph-icon-stroke-width: 1.75;
        @apply mx-auto h-4;
    }
}

.dp--action-row {
    @apply pt-4 px-2.5 flex justify-center;
}

.dp--action-button {
    @apply font-sans font-semibold leading-none text-sm px-3 py-2 rounded-md h-auto;
}

.dp--selection-preview {
    @apply text-xs text-gray-700;
}
</style>
